import { RequestStatus } from 'features/request/requestSlice';
import { useRequest } from 'features/request/useRequest';
import { useResetStoreData } from 'features/request/useResetStoreData';
import type { BimActKc2OtherTotalItemData } from 'gql/graphql';

import type { ColDef, RowClassRules } from 'ag-grid-community';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { bimActKC2OtherTotalsGql } from '../bimWorkOrderOtherActKC2Queries';
import { BimActKc2OtherTotalNameCellRenderer } from '../components/BimActKc2OtherTotalNameCellRenderer';
import { BimActKc2OtherTotalPriceCellRenderer } from '../components/BimActKc2OtherTotalPriceCellRenderer';

import { useBimActKC2OtherTotalInflation } from './useBimActKC2OtherTotalInflation';

interface IProps {
    actKC2Id: number;
    isVersionPage?: boolean;
    isActionDisabled?: boolean;
    isInactive?: boolean;
}

const REQUEST_ID = 'BimWorkOrderOtherActDetails';

export const useBimWorkOrderOtherActDetailsData = ({
    actKC2Id,
    isVersionPage,
    isActionDisabled,
    isInactive,
}: IProps) => {
    const dispatch = useDispatch();

    const requestVariables = useMemo(
        () => ({
            actKC2OtherId: actKC2Id,
        }),
        [actKC2Id],
    );

    const [makeReset] = useResetStoreData({
        requestId: REQUEST_ID,
    });

    useEffect(
        () => () => {
            makeReset(undefined);
        },
        [makeReset],
    );

    const [data, status, , makeRequest] = useRequest({
        requestId: REQUEST_ID,
        query: bimActKC2OtherTotalsGql,
        variables: requestVariables,
        withEffect: !!actKC2Id && !isVersionPage,
    });

    const bimActKC2OtherTotalsMakeRequest = useCallback(() => {
        makeReset(undefined);
        dispatch(makeRequest(requestVariables));
    }, [dispatch, makeRequest, makeReset, requestVariables]);

    const { bimUseActKC2OtherTotalInflationStatus, bimUseActKC2OtherTotalInflationRequest } =
        useBimActKC2OtherTotalInflation(actKC2Id, bimActKC2OtherTotalsMakeRequest);

    const isLoading = useMemo(
        () => status === RequestStatus.FETCHING || bimUseActKC2OtherTotalInflationStatus === RequestStatus.FETCHING,
        [bimUseActKC2OtherTotalInflationStatus, status],
    );

    const detailsColumns = useMemo<ColDef<BimActKc2OtherTotalItemData>[]>(
        () => [
            {
                headerName: 'Наименование работ, затрат',
                field: 'name',
                flex: 3,
                cellRenderer: BimActKc2OtherTotalNameCellRenderer,
                cellRendererParams: {
                    bimUseActKC2OtherTotalInflationRequest,
                    isVersion: isVersionPage,
                    isActionDisabled,
                    isInactive,
                },
            },
            {
                headerName: 'Всего',
                field: 'total',
                flex: 1,
                cellRenderer: BimActKc2OtherTotalPriceCellRenderer,
                cellClass: 'ag-right-aligned-cell',
            },
        ],
        [bimUseActKC2OtherTotalInflationRequest, isActionDisabled, isInactive, isVersionPage],
    );

    const rowClassRules = useMemo<RowClassRules>(
        () => ({
            boldWeight: ({ data: rowData }: { data: BimActKc2OtherTotalItemData }) =>
                rowData?.sortOrder !== 3 && !rowData?.isInflation,
            totalsRow: ({ data: rowData }: { data: BimActKc2OtherTotalItemData }) =>
                rowData?.sortOrder === data?.bimActKC2OtherTotals?.items?.length,
        }),
        [data?.bimActKC2OtherTotals?.items?.length],
    );

    return {
        detailsData: data?.bimActKC2OtherTotals?.items,
        detailsColumns,
        isLoading,
        rowClassRules,
    };
};
